/**
 * Shared helper functions for the Windows page
 */

import {
  WINDOW_START_DATES,
  USE_PRICING_TIER_FROM,
  STATUS_COLORS,
} from "../constants/windowConstants";

/**
 * Extract the value from a react-select option or return the value itself.
 * Handles the common pattern: value?.value ?? value
 * @param {Object|any} val - The value or react-select option object
 * @returns {any} The extracted value
 */
export const getSelectValue = (val) => val?.value ?? val;

/**
 * Get the label for a window start date value.
 * @param {number|null|undefined} value - The window start date value
 * @returns {string} The label or a fallback
 */
export const getWindowStartDateLabel = (value) => {
  if (value === null || value === undefined) return "—";
  const option = WINDOW_START_DATES.find((opt) => opt.value === value);
  return option ? option.label : `Day ${value}`;
};

/**
 * Get the label for a pricing tier value.
 * @param {string|null|undefined} value - The pricing tier value
 * @returns {string} The label or a fallback
 */
export const getPricingTierLabel = (value) => {
  if (!value) return "—";
  const option = USE_PRICING_TIER_FROM.find((opt) => opt.value === value);
  return option ? option.label : value;
};

/**
 * Get the status configuration (icon and color) for a given status.
 * @param {string} status - The status value
 * @returns {{icon: string|null, color: string}} The status configuration
 */
export const getStatusConfig = (status) => {
  const s = (status || "").toUpperCase();

  if (s === "ACTIVE") {
    return { icon: "fa-check", color: STATUS_COLORS.green };
  }
  if (s === "NEEDS_ATTENTION") {
    return { icon: "fa-exclamation", color: STATUS_COLORS.orange };
  }
  if (s === "INACTIVE") {
    return { icon: "fa-xmark", color: STATUS_COLORS.red };
  }
  return { icon: null, color: STATUS_COLORS.gray };
};

/**
 * Get the color for a status value (simplified version for grid indicator).
 * @param {string} status - The status value
 * @returns {string} The color hex code
 */
export const getColorForStatus = (status) => {
  const colors = {
    ACTIVE: STATUS_COLORS.green,
    NEEDS_ATTENTION: STATUS_COLORS.orange,
    INACTIVE: STATUS_COLORS.red,
  };
  return colors[(status || "").toUpperCase()] || "#ccc";
};
